import { optimalBlueProductSearch } from './optimalBlueService';
import { LoanCompare } from '../../classes/LoanCompare';
import { getHomeEquityRate } from '../homeEquityRateService';
import {
  isHeloc,
  isHeloan,
  isHeloan3020,
  isSecondLoanRolledIn,
} from '../../utils/Operations';
import {
  PROPERTY_TYPE_KEYWORDS, PROPERTY_TYPE_RESULTS, UNITS, ERROR_PROPERTY_TYPE,
} from '../../constants/propertyTypes';

export function getOccupancy(occupancyType) {
  if (occupancyType?.includes('PRIMARY')) {
    return 'PrimaryResidence';
  } if (occupancyType?.includes('VACATION')) {
    return 'SecondHome';
  } if (occupancyType?.includes('INVESTMENT')) {
    return 'InvestmentProperty';
  }
}

export function getPropertyType(propertyType) {
  const type = propertyType.toUpperCase();
  if (type?.includes(PROPERTY_TYPE_KEYWORDS.SINGLE_FAMILY)
    || type?.includes(PROPERTY_TYPE_KEYWORDS.RURAL_RESIDENCE)
    || type?.includes(PROPERTY_TYPE_KEYWORDS.MULTI_FAMILY)
    || type?.includes(PROPERTY_TYPE_KEYWORDS.APARTMENT)
    || type?.includes(PROPERTY_TYPE_KEYWORDS.TRIPLEX)
    || type?.includes(PROPERTY_TYPE_KEYWORDS.QUADPLEX)
    || type?.includes(PROPERTY_TYPE_KEYWORDS.DUPLEX)
  ) {
    return PROPERTY_TYPE_RESULTS.SINGLE_FAMILY;
  }
  if (type?.includes(PROPERTY_TYPE_KEYWORDS.CONDOMINIUM)) {
    return PROPERTY_TYPE_RESULTS.CONDO;
  }
  if (type?.includes(PROPERTY_TYPE_KEYWORDS.TOWNHOUSE)) {
    return PROPERTY_TYPE_RESULTS.TOWNHOUSE;
  }
  if (type?.includes(PROPERTY_TYPE_KEYWORDS.TIMESHARE)) {
    return PROPERTY_TYPE_RESULTS.TIMESHARE;
  }
  if (type?.includes(PROPERTY_TYPE_KEYWORDS.CONDOTEL)) {
    return PROPERTY_TYPE_RESULTS.CONDOTEL;
  }
  if (type?.includes(PROPERTY_TYPE_KEYWORDS.NON_WARRANTABLE_CONDO)) {
    return PROPERTY_TYPE_RESULTS.NON_WARRANTABLE_CONDO;
  }
  if (type?.includes(PROPERTY_TYPE_KEYWORDS.MODULAR)) {
    return PROPERTY_TYPE_RESULTS.MODULAR;
  }
  if (type?.includes(PROPERTY_TYPE_KEYWORDS.PUD)) {
    return PROPERTY_TYPE_RESULTS.PUD;
  }
  if (type?.includes(PROPERTY_TYPE_KEYWORDS.COOP)
    || type?.includes(PROPERTY_TYPE_KEYWORDS.CO_OPERATIVE)) {
    return PROPERTY_TYPE_RESULTS.COOP;
  }
  if (type?.includes(PROPERTY_TYPE_KEYWORDS.MANUFACTURED)
    || type?.includes(PROPERTY_TYPE_KEYWORDS.MOBILE_HOME)) {
    return PROPERTY_TYPE_RESULTS.MANUFACTURED;
  }
  if (type?.includes(PROPERTY_TYPE_KEYWORDS.VACANT_LAND)
    || type?.includes(PROPERTY_TYPE_KEYWORDS.OTHER)
    || type?.includes(PROPERTY_TYPE_KEYWORDS.AGRICULTURAL)
    || type?.includes(PROPERTY_TYPE_KEYWORDS.COMMERCIAL)
    || type?.includes(PROPERTY_TYPE_KEYWORDS.INDUSTRIAL)
    || type?.includes(PROPERTY_TYPE_KEYWORDS.RESIDENTIAL_MIS)
    || type?.includes(PROPERTY_TYPE_KEYWORDS.PARKING)
    || type?.includes(PROPERTY_TYPE_KEYWORDS.STORAGE)
    || type?.includes(PROPERTY_TYPE_KEYWORDS.COMMON_AREA)
  ) {
    return ERROR_PROPERTY_TYPE; // Error cases handled separately
  }
  return ''; // Default fallback
}

export function getNumberOfUnits(propertyType) {
  let numberOfUnits = UNITS.ONE; // Default value
  if (propertyType.includes(PROPERTY_TYPE_KEYWORDS.DUPLEX)) {
    numberOfUnits = UNITS.TWO;
  } else if (propertyType.includes(PROPERTY_TYPE_KEYWORDS.TRIPLEX)) {
    numberOfUnits = UNITS.THREE;
  } else if (propertyType.includes(PROPERTY_TYPE_KEYWORDS.QUADPLEX)) {
    numberOfUnits = UNITS.FOUR;
  }
  return numberOfUnits;
}

export function getLoanType(loanType) {
  if (loanType?.includes('FHA')) {
    return 'FHA';
  } if (loanType?.includes('VA')) {
    return 'VA';
  }
  return 'Conforming';
}

export function getLoanPurpose(loanType) {
  if (loanType?.includes('FHA')) {
    return 'FHAStreamlineRefi';
  } if (loanType?.includes('VA')) {
    return 'VARateReduction';
  }
  return 'RefiCashout';
}

export function getLoanPurposeFromCard(cardName) {
  const normalized = cardName?.toLowerCase() ?? '';
  if (normalized.includes('streamlined')) {
    return 'FHAStreamlineRefi';
  }
  if (normalized.includes('irrl')) {
    return 'VARateReduction';
  }
  if (normalized.includes('fha')) {
    return 'RefiRateTermLimitedCO';
  }
  if (normalized.includes('va')) {
    return 'RefiRateTermLimitedCO';
  }
  return 'RefiRateTermLimitedCO';
}

export function resolveRefiLoanPurpose(loanName, isCashOut, loanAssumption) {
  const normalized = loanName?.toLowerCase() ?? '';
  const isConventional = !normalized.includes('fha') && !normalized.includes('va');
  const cashOut = isCashOut || isSecondLoanRolledIn(loanAssumption);
  return (cashOut && isConventional) ? 'RefiCashout' : getLoanPurposeFromCard(loanName);
}

const convertYearsToNumber = (years) => {
  let yearsInCard;
  if (years === 'ThirtyYear') {
    yearsInCard = '30';
  } else if (years === 'TwentyYear') {
    yearsInCard = '20';
  } else if (years === 'FifteenYear') {
    yearsInCard = '15';
  } else if (years === 'TenYear') { // arm
    yearsInCard = '10';
  } else if (years === 'SevenYear') { // arm
    yearsInCard = '7';
  } else if (years === 'FiveYear') { // arm
    yearsInCard = '5';
  } else if (years === 'ThreeYear') { // arm
    yearsInCard = '3';
  }
  return yearsInCard;
};

export const getInputFromName = (selectedLoan) => {
  const cardItems = selectedLoan.split(' ');
  // this is for HELOAC/HELOAN which is unsupported
  if (cardItems.length <= 1) return false;

  let amortizationTypeInCard = cardItems[2]; // ARM/Fixed
  if (amortizationTypeInCard === 'fixed') {
    amortizationTypeInCard = 'Fixed';
  }

  const loanTypeInput = cardItems.length >= 4 ? cardItems[3] : 'Conforming';

  return {
    amortizationType: [amortizationTypeInCard],
    loanType: loanTypeInput,
  };
};

// product = object returned by Optimal Blue
const getProductName = (product) => {
  let yearsString = '';
  let loanType = '';
  let amortizationType = product.amortizationType.toUpperCase();
  if (amortizationType === 'FIXED') {
    amortizationType = amortizationType.toLowerCase();
    yearsString = product.amortizationTerm;
    const tempLoanType = product.loanType.toUpperCase();
    if (tempLoanType === 'FHA' || tempLoanType === 'VA') loanType = ` ${tempLoanType}`;
  } else {
    yearsString = product.armFixedTerm;
  }
  const years = convertYearsToNumber(yearsString);
  return `${years} year ${amortizationType}${loanType}`;
};

export const invokeOptimalBlue = async (input, currentLoanAssumptions, closingCost, refiReason, ltv = 0) => {
  const loanAmount = closingCost?.fundsAtClosing?.calculatedLoanAmount;
  const estimatedHomeValue = currentLoanAssumptions?.estimatedHomeValue;
  const ficoScore = input?.loanInformation?.representativeFICO;
  const inputBaseLoanAmount = input?.loanInformation?.baseLoanAmount;

  const hasRequiredInputs = !!loanAmount && !!estimatedHomeValue && !!ficoScore
    && Number.isFinite(inputBaseLoanAmount) && inputBaseLoanAmount > 0;
  const results = {
    message: '',
    data: [],
  };

  if (!hasRequiredInputs) {
    results.message = 'WARNING';
    return results; // prevents 400 error from sending invalid request
  }

  const response = await optimalBlueProductSearch(input);

  const tempResult = [];
  if (response?.status === 200) {
    if (response?.data?.messages) {
      results.message = response?.data?.messages[0].message;
      return results;
    }
    response?.data?.products?.forEach((product) => {
      const productName = getProductName(product);
      tempResult.push(new LoanCompare(product, currentLoanAssumptions, closingCost?.fundsAtClosing?.calculatedLoanAmount, closingCost?.fundsAtClosing?.initialDrawFromLoan, productName, ltv, refiReason));
    });
  } else {
    results.message = 'ERROR';
    return results;
  }

  // Group loans by selectedLoan type
  const loanGroups = new Map();
  tempResult.forEach((loan) => {
    const type = loan.selectedLoan;
    if (!loanGroups.has(type)) {
      loanGroups.set(type, []);
    }
    loanGroups.get(type).push(loan);
  });

  results.data = loanGroups;

  return results;
};

// Home Equity Loan Fixed Rate 60-240 month     - (HELOAN)
// Home Equity Line of Credit
// Home Equity Line of Credit (Rental / Investment)
// occupancy: in currentLoanAssumptions
// ltv: new input
// ficoScore: inside input(loanInformation.representativeFICO)
export const invokeHomeEquityRate = async (homeEquityValues, ltv, creditScore, currentLoanAssumptions, cashRequested, refiReason, closingCosts) => {
  const results = {
    message: '',
    data: [],
  };
  let loanType;

  if (homeEquityValues.length === 0) return results;

  await Promise.all(homeEquityValues.map(async (selectedLoan) => {
    if (isHeloan(selectedLoan)) {
      loanType = 'Home Equity Loan Fixed Rate 60-240 months';
    } else if (isHeloc(selectedLoan)) {
      if (currentLoanAssumptions?.occupancyType?.includes('INVESTMENT')) {
        loanType = 'Home Equity Line of Credit (Rental / Investment)';
      } else {
        loanType = 'Home Equity Line of Credit';
      }
    } else if (isHeloan3020(selectedLoan)) {
      loanType = 'Home Equity Loan Fixed Rate 30/20';
    }

    const response = await getHomeEquityRate(loanType, ltv, creditScore);

    let rate = 0;
    if (response?.status === 200) {
      if (response?.data?.interest_rate) {
        rate = JSON.parse(response?.data?.interest_rate);
      } else {
        results.message = response?.data?.message;
      }
      const data = {
        rate,
        apr: 0, // TODO: add here the apr calculation
      };

      results.data.push(new LoanCompare(data, currentLoanAssumptions, cashRequested, cashRequested, selectedLoan, ltv, refiReason, closingCosts));
    } else {
      results.message = 'ERROR';
    }
  }));

  return results;
};

export const filterCard = (allItems, productName, rateChanged = false, currentLoanData = null, selectedRate = null, occupancy = null, totalLoanAmount = 0) => {
  if (!allItems || allItems.length === 0) {
    return currentLoanData || new LoanCompare();
  }

  // --- Case 1: User manually changed the rate ---
  if (rateChanged && currentLoanData) {
    return currentLoanData;
  }

  // Streamlined/IRRL options map to their base product name in OB results
  const lookupName = productName?.replace(/\s+(streamlined|irrl)$/i, '').trim() || productName;
  const isAliased = lookupName?.toUpperCase()?.trim() !== productName?.toUpperCase()?.trim();

  // --- Case 2: all items are already LoanCompare objects ---
  const allAreLoanCompare = allItems.every((i) => i instanceof LoanCompare);

  if (allAreLoanCompare) {
    const match = allItems.find((i) => i.selectedLoan?.toUpperCase()?.trim() === lookupName?.toUpperCase()?.trim());
    if (match && isAliased) {
      const aliased = Object.assign(new LoanCompare(), match);
      aliased.selectedLoan = productName;
      return aliased;
    }
    return match || new LoanCompare();
  }

  // --- Case 3: Full dataset from OB (array of Maps) ---
  const matches = [];
  allItems.forEach((item) => {
    if (item instanceof Map) {
      item.forEach((loanArray, key) => {
        if (key?.toUpperCase()?.trim() === lookupName?.toUpperCase()?.trim()) {
          loanArray.forEach((loan) => matches.push(loan));
        }
      });
    } else if (item instanceof LoanCompare) {
      if (item.selectedLoan?.toUpperCase()?.trim() === lookupName?.toUpperCase()?.trim()) {
        matches.push(item);
      }
    }
  });

  if (matches.length === 0) return new LoanCompare();
  const clonedMatches = matches.map((loan) => Object.assign(new LoanCompare(), loan));

  // --- Apply occupancy-based Chase filter ---
  const CONFORMING_LOAN_LIMIT = 832750;
  const occupancyUpper = occupancy?.toUpperCase()?.trim();
  const isChaseAllowed = occupancyUpper?.includes('VACATION') || occupancyUpper?.includes('INVESTMENT') || totalLoanAmount > CONFORMING_LOAN_LIMIT;

  const occupancyFiltered = isChaseAllowed ? clonedMatches : clonedMatches.filter((loan) => !loan.productName?.toUpperCase()?.trim().startsWith('CHASE'));

  // --- Apply price range filter [97, 102.125] ---
  const priceFiltered = occupancyFiltered.filter((l) => l.price >= 97 && l.price <= 102.125);

  if (priceFiltered.length === 0) return new LoanCompare();

  // --- Pick default loan: closest to 100 (prefer >= 100), then lowest rate, tie-break higher price ---
  const above100 = priceFiltered.filter((l) => l.price >= 100);
  const validLoans = above100.length > 0 ? above100 : priceFiltered;

  const defaultLoan = validLoans.reduce((best, current) => {
    const diffCurrent = Math.abs(current.price - 100);
    const diffBest = Math.abs(best.price - 100);

    if (diffCurrent < diffBest) return current;
    if (diffCurrent > diffBest) return best;

    // Same distance to 100 → lowest rate
    if (current.interestRate < best.interestRate) return current;
    if (current.interestRate > best.interestRate) return best;

    // Same rate → higher price
    return current.price > best.price ? current : best;
  });

  // --- Build rate options ---
  const allMatchesSorted = [...priceFiltered].sort((a, b) => a.interestRate - b.interestRate);

  const rateMap = new Map();
  allMatchesSorted.forEach((loan) => {
    const rateKey = Number(loan.interestRate).toFixed(3);
    const existing = rateMap.get(rateKey);
    if (!existing || loan.price > existing.price) {
      rateMap.set(rateKey, loan);
    }
  });

  const uniqueRateOptions = Array.from(rateMap.values());

  // --- Map rate options ---
  defaultLoan.allRateOptions = uniqueRateOptions.map((loan) => {
    const { allRateOptions, ...rest } = loan;
    return Object.assign(new LoanCompare(), rest);
  });

  // If we have a selectedRate, try to respect it
  if (selectedRate != null && selectedRate !== '' && defaultLoan.allRateOptions) {
    // Find the index of the matching rate inside allRateOptions
    const index = defaultLoan.allRateOptions.findIndex((l) => Number(l.interestRate).toFixed(3) === Number(selectedRate).toFixed(3));
    if (index !== -1) {
      const matchedLoan = defaultLoan.allRateOptions[index];

      // Copy all relevant fields from the matched loan into defaultLoan
      Object.assign(defaultLoan, matchedLoan);

      // Keep explicit tracking for UI
      defaultLoan.selectedRateIndex = index;
      defaultLoan.selectedRate = selectedRate;
    } else {
      // Saved rate not available: use the next rate up (higher interest rate = higher price/rebate)
      const fallbackIndex = defaultLoan.allRateOptions.findIndex(
        (l) => Number(l.interestRate).toFixed(3) > Number(selectedRate).toFixed(3),
      );
      const resolvedIndex = fallbackIndex !== -1 ? fallbackIndex : defaultLoan.allRateOptions.length - 1;
      const fallbackLoan = defaultLoan.allRateOptions[resolvedIndex];
      Object.assign(defaultLoan, fallbackLoan);
      defaultLoan.selectedRateIndex = resolvedIndex;
      defaultLoan.selectedRate = fallbackLoan.interestRate;
    }
  } else {
    // No selectedRate provided, use default selection
    defaultLoan.selectedRateIndex = uniqueRateOptions.findIndex(
      (loan) => Number(loan.interestRate).toFixed(3) === Number(defaultLoan.interestRate).toFixed(3),
    );
  }

  // Restore the original card name for streamlined/IRRL products
  if (isAliased) {
    defaultLoan.selectedLoan = productName;
    defaultLoan.allRateOptions = defaultLoan.allRateOptions?.map((opt) => ({ ...opt, selectedLoan: productName }));
  }

  return defaultLoan;
};
